/*
 * Hercule — Galerie produit (PDP)
 * 1) Dots de navigation MOBILE uniquement (mode slider). Desktop = thumbnails + chevrons natifs.
 * 2) Autoplay des vidéos : muted + playsinline, joué seulement quand le slide est en vue.
 *    Perf mobile : la vidéo est PRÉCHARGÉE/bufferisée dès qu'elle approche du viewport
 *    (une largeur d'écran avant), pas au moment où elle devient visible — sinon le
 *    téléchargement (preload:none dans le template) démarrait trop tard et la vidéo
 *    « mettait trop de temps à se lancer » au swipe. Lecture quand ≥50% en vue, pause sinon.
 *    Le loop reste piloté par le setting produit (data-enable-video-looping).
 */
(() => {
  'use strict';
  const MOBILE = '(max-width: 749px)';

  /* -------------------------------------------------------------------- */
  /* 1. Dots mobile                                                        */
  /* -------------------------------------------------------------------- */
  class ProductSliderDots extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('product-images');
      this.scroller = this.gallery && this.gallery.querySelector('.product__slides, [data-product-media-list]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!this.scroller || this.dots.length === 0) return;

      this.slides = Array.from(this.scroller.querySelectorAll('.product__slide, [data-slide]'));

      // Clic sur un dot -> scroll horizontal vers le slide correspondant.
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const slide = this.slideForId(dot.getAttribute('data-media-id'));
          if (!slide) return;
          const delta = slide.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left;
          this.scroller.scrollTo({ left: this.scroller.scrollLeft + delta, behavior: 'smooth' });
        });
      });

      this.mq = window.matchMedia(MOBILE);
      this.onMq = () => this.sync();
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
      else this.mq.addListener(this.onMq); // Safari < 14
      this.sync();
    }

    disconnectedCallback() {
      if (this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
        else this.mq.removeListener(this.onMq);
      }
      if (this.io) this.io.disconnect();
    }

    slideForId(id) {
      return this.slides.find((s) => s.getAttribute('data-media-id') === id);
    }

    // N'observe qu'en mobile (les dots sont masqués en CSS ailleurs).
    sync() {
      if (this.mq.matches) {
        if (this.io) return;
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && e.intersectionRatio >= 0.5) {
                this.setActive(e.target.getAttribute('data-media-id'));
              }
            });
          },
          { root: this.scroller, threshold: [0.5, 0.75] }
        );
        this.slides.forEach((s) => this.io.observe(s));
      } else if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
    }

    setActive(id) {
      this.dots.forEach((d) => d.classList.toggle('is-active', d.getAttribute('data-media-id') === id));
    }
  }

  if (!customElements.get('product-slider-dots')) {
    customElements.define('product-slider-dots', ProductSliderDots);
  }

  /* -------------------------------------------------------------------- */
  /* 2. Autoplay vidéo en vue (muted, perf conservée)                     */
  /* -------------------------------------------------------------------- */
  // Injecte la vidéo (clone du <template>) via le poster — idempotent.
  function injectVideo(slide) {
    const deferred = slide.querySelector('deferred-media, .deferred-media');
    if (!deferred) return null;
    if (!deferred.querySelector('video, iframe')) {
      const btn = deferred.querySelector('[data-deferred-media-button]');
      if (btn) btn.click();
    }
    return deferred;
  }

  // Précharge : injecte + bufferise la vidéo AVANT qu'elle soit à l'écran.
  // Le <template> pose preload:none ; on repasse en preload:auto + load() pour que
  // le téléchargement soit déjà bien engagé quand l'utilisateur arrive sur le slide.
  function prime(slide) {
    const deferred = injectVideo(slide);
    if (!deferred) return;
    const buffer = () => {
      const video = deferred.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      if (video.preload !== 'auto') video.preload = 'auto';
      // readyState < 2 = pas encore assez de données pour la 1re frame -> on force le buffer.
      if (video.readyState < 2) {
        try {
          video.load();
        } catch (e) {}
      }
    };
    buffer();
    requestAnimationFrame(buffer);
  }

  function configureAndPlay(slide) {
    const deferred = injectVideo(slide);
    if (!deferred) return;

    // La vidéo peut être injectée au frame suivant -> on tente immédiatement puis en rAF.
    const apply = () => {
      const video = deferred.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.loop = true;
      video.controls = false;
      video.removeAttribute('controls');
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    apply();
    requestAnimationFrame(apply);
  }

  function pause(slide) {
    const video = slide.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  function setupVideoAutoplay(gallery) {
    if (gallery.dataset.herculeVideoBound === '1') return;
    const videoSlides = Array.from(gallery.querySelectorAll('[data-video]'));
    if (videoSlides.length === 0) return;
    gallery.dataset.herculeVideoBound = '1';

    // 1) Préchargement anticipé — MOBILE uniquement (slider tactile) : c'est là que
    //    la vidéo « mettait trop de temps à se lancer » au swipe. On bufferise dès
    //    qu'elle approche (≈ une largeur de viewport avant), une seule fois par slide.
    //    Desktop (fader + vignettes) : chargement à la demande au clic, on n'alourdit
    //    pas le 1er rendu desktop (cf. sensibilité perf vidéos autoplay).
    const mq = window.matchMedia(MOBILE);
    let preloadIo = null;
    const bindPreload = () => {
      if (preloadIo || !mq.matches) return;
      preloadIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              prime(e.target);
              preloadIo.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 120% 0px 120%', threshold: 0 }
      );
      videoSlides.forEach((s) => preloadIo.observe(s));
    };
    bindPreload(); // au chargement si déjà en mobile
    if (mq.addEventListener) mq.addEventListener('change', bindPreload);
    else mq.addListener(bindPreload); // Safari < 14

    // 2) Lecture : joue quand ≥50% visible, pause sinon (desktop + mobile).
    const playIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) configureAndPlay(e.target);
          else pause(e.target);
        });
      },
      { threshold: [0, 0.5] }
    );
    videoSlides.forEach((s) => playIo.observe(s));
  }

  function init() {
    document.querySelectorAll('product-images').forEach(setupVideoAutoplay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
